import React, {
	type ComponentProps,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { graphql } from 'react-relay';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import { useIntl } from 'react-intl-next';
import { Subscribe } from 'unstated';

import { AnalyticsListener, useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';
import type { KeyPhrasesQueryType, KeyPhrasesQueryVariables } from '@atlassian/search-ai';
import { AiBrand, useDefinitionSupplier } from '@atlassian/search-ai';
import { RendererDefinitionTextHighlighter, getHighlightRegex } from '@atlassian/search-ai/v2';

import { createLinkEventHandler } from '@confluence/adf-renderer/entry-points/createLinkEventHandler.ts';
import { createSmartCardEventHandler } from '@confluence/adf-renderer/entry-points/createSmartCardEventHandler.ts';
import { type ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { useRendererAnnotationProviderOptions } from '@confluence/annotation-provider/entry-points/useRendererAnnotationProviderOptions.ts';
import { useDocumentUpdateStatusDispatch } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { useUpdateDocumentActions } from '@confluence/comment-context/entry-points/useUpdateDocument.ts';
import { useReattachComment } from '@confluence/comment-context/entry-points/ReattachCommentContext.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useUpdateAnnotations } from '@confluence/comments-data/entry-points/useUpdateAnnotations.ts';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import { getConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getConfluenceContentAri.ts';
import { ContentBlogUnifiedQuery, ContentUnifiedQuery } from '@confluence/content-unified-query';
import { isInPreviewPanel } from '@confluence/preview-panel-api/entry-points/previewPanelUtils.ts';
import { InvisibleLoadingContainer as ReadingAidsInvisibleLoadingPortal } from '@confluence/contextual-reading-aids/entry-points/ReadingAidsLoadingContainer.ts';
import { RendererTextHighlighter } from '@confluence/contextual-reading-aids/entry-points/RendererTextHighlighter.ts';
import { PlatformHighlightKeywordsQuery } from '@confluence/contextual-reading-aids/entry-points/preloadAutohighlights.ts';
import { getHighlightCategories } from '@confluence/contextual-reading-aids/entry-points/reading-aids-utils.ts';
import { useDefinitionRestrictionOptions } from '@confluence/contextual-reading-aids/entry-points/useDefinitionRestrictionOptions.ts';
import { getAutoConvertionInfo } from '@confluence/editor-conversion/entry-points/getAutoConvertionInfo.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { AI_DEFINITIONS_AUTOHIGHLIGHT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { InlineCommentNudge } from '@confluence/experiment-inline-comment-nudge';
import { ExtrensionIndexProvider } from '@confluence/extensions-performance/entry-points/ExtensionIndex.ts';
import { MultiMacroQueryContainer } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';
import { fg } from '@confluence/feature-gating';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { useExtensionViewportSizes } from '@confluence/forge-ui/entry-points/useExtensionViewportSizes.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import { InlineCommentLinkHandler } from '@confluence/inline-comments-link-handler';
import { ReattachCommentPanelLoader } from '@confluence/inline-comments-reattach';
import { MediaAnalytics } from '@confluence/media-analytics';
import { MentionScrollComponent } from '@confluence/mention';
import { TaskScrollComponent } from '@confluence/task/entry-points/TaskScrollComponent.ts';
import {
	useHash,
	useMatchRoute,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { CONTENT_RENDERER_METRIC } from '@confluence/view-content-perf-metrics/entry-points/perf.config.ts';
import { RendererTTIMarker } from '@confluence/view-content-perf-metrics/entry-points/RendererTTIMarker.ts';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { getCalculatedHeightFromNode } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/getCalculatedHeightFromNode.ts';
import { usePrintIntercept } from '@confluence/pdf-print-utils/entry-points/print.ts';
import { usePrintCoverInjector } from '@confluence/pdf-print-utils/entry-points/coverInjector.ts';

import { WithAnnotationsWrapper } from '../WithAnnotationsWrapper.tsx';
import { handleAnalyticsEvents } from '../handleAnalyticsEvents.ts';
import { useReadAndWriteContentQueryDataCache } from '../hooks/useReadAndWriteContentQueryDataCache.tsx';

import type { PageContentRendererFabricProps } from './PageContentRendererFabricProps.ts';
import { LegacyWrmRenderer } from './LegacyWrmRenderer.tsx';
import { StyledRenderer } from './StyledRenderer.tsx';
import { useADFDocument } from './useADFDocument.ts';
import { useAdfStats } from './useAdfStats.ts';
import { useCreateSerializer } from './progressive/useCreateSerializer.ts';
import { useExtensionHandlersForPageContentRendererFabric } from './useExtensionHandlersForPageContentRendererFabric.ts';

const fabricErrorBoundaryAttributes = {
	fabric: true,
};

const WORKSPACE_RESOURCE_TYPE = 'workspace';

export const PageContentRendererFabricInternal = (props: PageContentRendererFabricProps) => {
	const {
		content,
		contentId,
		contentBody,
		rootEl,
		setRootEl,
		hasInlineComments,
		hasProgressiveRendering,
		isSpaceOverview,
		eventHandlerOverrides,
		spaceKey,
		spaceId,
		renderPerformanceMetrics,
		revision,
		contentAppearance,
		markerRefs,
		queryHash,
		isArchived,
		contentStatusError,
		isContentHeaderFixedAtTop,
		customPerfData,
		isPresentational,
		document,
		...rest
	} = props;

	const { featureFlags, cloudId, userId, locale, activationId, isRovoEnabled, isRovoLLMEnabled } =
		useSessionData();
	const { setDocumentUpdated } = useDocumentUpdateStatusDispatch();
	const { setUpdateDocumentFn } = useUpdateDocumentActions();
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [state] = useContentState();
	const localRef = useRef<HTMLDivElement>(null);
	const [renderComplete, setRenderComplete] = useState(false);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const pageMode = useGetPageMode();

	usePrintIntercept({
		enabled: expValEquals('afm-enable-command-p-reroute', 'isEnabled', true),
		shouldBlockPrint: () => false,
	});
	usePrintCoverInjector(expValEquals('afm-enable-command-p-reroute', 'isEnabled', true), intl);

	const readFragmentInterop = useReadFragmentInterop<any>({
		fragmentSpec: expValEquals('confluence_fetch_appkey_field', 'isEnabled', true)
			? graphql`
					fragment PageContentRendererFabricReadFragmentWithAppKey on MacroConnection {
						nodes {
							macroId
							contentId
							renderedMacro(mode: RENDERER) {
								appKey
								value
								macroRenderedRepresentation
								macroBodyStorage
								webResource {
									contexts
									keys
									superbatch {
										metatags
									}
									tags {
										css
										data
										js
									}
									uris {
										css
										data
										js
									}
								}
								mediaToken {
									token
								}
							}
						}
						pageInfo {
							endCursor
							hasNextPage
							__typename
						}
					}
				`
			: graphql`
					fragment PageContentRendererFabricReadFragment on MacroConnection {
						nodes {
							macroId
							contentId
							renderedMacro(mode: RENDERER) {
								value
								macroRenderedRepresentation
								macroBodyStorage
								webResource {
									contexts
									keys
									superbatch {
										metatags
									}
									tags {
										css
										data
										js
									}
									uris {
										css
										data
										js
									}
								}
								mediaToken {
									token
								}
							}
						}
						pageInfo {
							endCursor
							hasNextPage
							__typename
						}
					}
				`,
	});

	const { push } = useRouteActions();
	const routeHashValue = useHash();
	const matchRoute = useMatchRoute();
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	const contentType = content?.type;
	const lastModifiedDate = content?.metadata?.lastModifiedDate;
	// adfContentId is the content ID associated with the loaded ADF document. In contrast, contentId is the content ID from the URL.
	// This distinction is important because during page transitions we keep the old ADF document loaded while loading the new one.
	const adfContentId = content?.id;
	const adf = contentBody?.value || '';
	const isFabric = contentBody?.representation === 'atlas_doc_format';
	const { canAddComments } = useAddCommentPermissionCheck(contentId);

	const commentResidesInPreviewPanel = fg('confluence_comment_disable_preview_panel_nudgebar')
		? isInPreviewPanel()
		: false;

	const displayInlineCommentNudger =
		canAddComments && hasInlineComments && !isSpaceOverview && !commentResidesInPreviewPanel;
	const isReadingAidsAutoHighlightEnabled =
		(isAIEnabledForContent || isRovoEnabledForContent) &&
		locale.startsWith('en') &&
		!JSON.parse(
			localStorage.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT) || '""',
		);

	const cloudIdARI = ConfluenceSiteAri.create({
		siteId: cloudId,
	}).toString();

	const contentARI = contentType
		? getConfluenceContentAri({
				siteId: cloudId,
				resourceType: contentType,
				resourceId: contentId,
				activationId: '',
			})
		: '';

	const spaceARI = spaceId
		? getConfluenceContentAri({
				siteId: cloudId,
				resourceType: 'space',
				resourceId: spaceId,
				activationId: '',
			})
		: '';

	const workspaceId = getConfluenceContentAri({
		siteId: cloudId,
		resourceType: WORKSPACE_RESOURCE_TYPE,
		resourceId: activationId,
		activationId: '',
	});

	const {
		data: queryData,

		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
	} = useQuery<KeyPhrasesQueryType, KeyPhrasesQueryVariables>(PlatformHighlightKeywordsQuery, {
		variables: {
			workspaceId,
			entityAri: contentARI,
		},
		client: getAGGClient(),
		skip: !isReadingAidsAutoHighlightEnabled,
	});

	const keyPhrases = useMemo(
		() => queryData?.knowledgeDiscovery?.keyPhrases?.nodes ?? [],
		[queryData],
	);
	const autoHighlightRegex = useMemo(
		() => getHighlightRegex(queryData?.knowledgeDiscovery?.keyPhrases?.nodes ?? []),
		[queryData],
	);

	const { fetchDefinition } = useDefinitionSupplier({
		workspaceId,
		cloudIdARI,
		fetchConfig: {
			entity_ari: contentARI,
		},
		productConfig: {
			contentId: contentARI,
			spaceId: spaceARI,
		},
		isDefinitionCurationEnabled: true,
		product: 'confluence',
	});

	const onDisableDefinitions = useCallback(() => {
		localStorage.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT, true);
	}, []);

	const onEnableDefinitions = useCallback(() => {
		localStorage.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT, false);
	}, []);

	const getDefinitionsDisabled = useCallback(
		() =>
			localStorage.getItemAsBoolean(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT),
		[],
	);

	const definitionRestrictionOptions = useDefinitionRestrictionOptions(contentType ?? undefined);

	const [hideHeadingIds, setHideHeadingIds] = useState(Boolean(routeHashValue));
	const extensionViewportSizes = useExtensionViewportSizes(false);
	const renderServerPlaceholder = useRenderServerPlaceholder(true);

	useEffect(() => {
		if (hideHeadingIds) {
			const t = setTimeout(() => {
				setHideHeadingIds(false);
			}, 0);
			return () => clearTimeout(t);
		}
	}, [hideHeadingIds]);

	useEffect(() => {
		if (isReadingAidsAutoHighlightEnabled) {
			experienceTracker.start({
				name: AI_DEFINITIONS_AUTOHIGHLIGHT_EXPERIENCE,
				attributes: {
					contentId,
				},
			});
			try {
				experienceTracker.succeed({
					name: AI_DEFINITIONS_AUTOHIGHLIGHT_EXPERIENCE,
					attributes: {
						contentId,
					},
				});
			} catch (error) {
				experienceTracker.fail({
					name: AI_DEFINITIONS_AUTOHIGHLIGHT_EXPERIENCE,
					attributes: {
						contentId,
					},
					error,
				});
			}

			if (!fg('kd_definitions_click_and_show_event_product_parity')) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'shown',
						actionSubject: 'readingAidsAcronymHighlights',
						source: 'viewPageScreen',
						attributes: {
							contentId,
							numHighlights: keyPhrases.length,
							categories: getHighlightCategories(keyPhrases),
							beAcronymsEnabled: true,
						},
					},
				}).fire();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);

	const { updateUnresolvedInlineComment } = useInlineCommentsActions();
	const { setOrderedActiveAnnotationIdList } = useCommentsDataActions();
	const { isInReattachMode } = useReattachComment();

	const eventHandlers = useMemo(
		() => ({
			...createSmartCardEventHandler({ push, matchRoute }),
			...createLinkEventHandler({ push, matchRoute }),
			...eventHandlerOverrides,
		}),
		[push, matchRoute, eventHandlerOverrides],
	);

	const isAutoConverted = useMemo(
		() =>
			fg('confluence_editor_auto-convert_changes')
				? getAutoConvertionInfo(content).isAutoConverted
				: !getAutoConvertionInfo(content).editorProperty,
		[content],
	);

	/**
	 * Inline Edit section starts
	 */
	const [documentFromHook] = useADFDocument(adf, isFabric);
	const adfDocument = document ?? documentFromHook;
	const adfStats = useAdfStats(adfDocument, customPerfData);
	const createSerializer = useCreateSerializer({
		skip: !hasProgressiveRendering,
		totalNodeCount: adfStats.totalNodeCount,
		nodeCountByType: adfStats.nodeCountByType,
		adfContentId: adfContentId || undefined,
	});

	const chooseContentUnifiedQuery =
		contentType === 'blogpost' ? ContentBlogUnifiedQuery : ContentUnifiedQuery;

	const readAndWriteQueryCache = useReadAndWriteContentQueryDataCache({
		query: chooseContentUnifiedQuery,
		contentId,
		spaceKey,
	});

	/**
	 * Inline Edit section ends
	 */

	// e.g. this runs when a new top level comment is made on the page
	const updateDocument = useCallback(
		(newAdfDoc: JSONDocNode) => {
			readAndWriteQueryCache(newAdfDoc);
			setDocumentUpdated(true);
			// Give document time to update
			// TODO: turn this into an async function
			setTimeout(() => {
				setDocumentUpdated(false);
			}, 1500);
		},
		[readAndWriteQueryCache, setDocumentUpdated],
	);

	useEffect(() => {
		// Set the updateDocument function whenever the renderer mounts with a new contentId
		setUpdateDocumentFn(updateDocument);
	}, [contentId, setUpdateDocumentFn, updateDocument]);

	const updateUnresolvedInlineCommentFn = useUpdateAnnotations([
		updateUnresolvedInlineComment,
		setOrderedActiveAnnotationIdList,
	]);

	const rendererAnnotationProviderOptions = useRendererAnnotationProviderOptions({
		pageId: contentId,
		validAnnotationsCallback: updateUnresolvedInlineCommentFn,
		triggerAnalyticsEvent: createAnalyticsEvent,
		updateDocument,
		isArchived,
	});

	const annotationProvider = hasInlineComments ? rendererAnnotationProviderOptions : undefined;

	const { mode } = useEditorContentModeValue({ contentId, isArchived });

	let contentMode;
	if (expValEquals('cc_editor_ai_content_mode', 'forceAll', true)) {
		contentMode = 'compact';
	} else if (
		fg('platform_editor_content_mode_button_mvp') ||
		expValEquals('confluence_compact_text_format', 'isEnabled', true)
	) {
		contentMode = mode;
	} else {
		contentMode = undefined;
	}

	const { dataProviders, extensionHandlers } = useExtensionHandlersForPageContentRendererFabric(
		{ ...props, contentMode },
		annotationProvider,
	);

	useEffect(() => {
		setRenderComplete(false);
	}, [contentId]);

	const handleOnComplete: ADFRendererProps['onComplete'] = useCallback(() => {
		window.__RENDERER_NODES_COUNT__ = adfStats.nodeCountByType;
		setRenderComplete(true);
	}, [adfStats.nodeCountByType]);

	const memoRendererTTIMarkerRenderWhenReady: ADFRendererProps['renderWhenReady'] = useMemo(
		() =>
			renderPerformanceMetrics({
				contentId,
				isFabric: true,
				appearance: contentAppearance,
				revision,
				isRenderTTIMark: true,
				isAutoConverted,
				lastModifiedDate,
				customPerfData,
			}),
		[
			contentId,
			contentAppearance,
			revision,
			isAutoConverted,
			lastModifiedDate,
			customPerfData,
			renderPerformanceMetrics,
		],
	);

	const memoAdfRendererRenderWhenReady: ADFRendererProps['renderWhenReady'] = useMemo(
		() =>
			renderPerformanceMetrics({
				isFabric: true,
			}),
		[renderPerformanceMetrics],
	);

	// This is used to prevent re-renders of the text highlighter by converting all deps into a string formatted const
	const textHighlighterDependency =
		isReadingAidsAutoHighlightEnabled + autoHighlightRegex.toString() + keyPhrases?.length;

	const memoTextHighlighter: ADFRendererProps['textHighlighter'] = useMemo(
		() =>
			isReadingAidsAutoHighlightEnabled
				? {
						pattern: autoHighlightRegex,
						component: (props) => <RendererTextHighlighter {...props} keyPhrases={keyPhrases} />,
					}
				: undefined,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[textHighlighterDependency],
	);

	const memoTextPlatformHighlighter: ADFRendererProps['textHighlighter'] = useMemo(
		() => {
			type RendererDefinitionTextHighlighterProps = ComponentProps<
				typeof RendererDefinitionTextHighlighter
			>;
			const sourceConfig: RendererDefinitionTextHighlighterProps['sourceConfig'] = {
				brand: isRovoEnabled ? AiBrand.ROVO : AiBrand.AI,
				cloudId,
				contentARI,
				contentId,
				experience: 'contentAutohighlight',
				source: 'readingAids',
				sourceProduct: 'confluence',
				userId: userId ?? '',
				workspaceId,
			};

			const curatedDefinitionScopeConfig = {
				workspaceId,
				definitionRestrictionOptions,
				productScopes: {
					contentARI,
					spaceARI,
				},
				cloudId,
				product: 'confluence',
			};

			const definitionConfig = {
				fetchDefinition,
				isAutohighlighted: true,
				definitionCurationOptions: { curatedDefinitionScopeConfig },
				disableDefinitionsCallbacks: {
					onDisableDefinitions,
					onEnableDefinitions,
					getDefinitionsDisabled,
				},
				showChatFollowUps: isRovoLLMEnabled,
			};

			return isReadingAidsAutoHighlightEnabled
				? {
						pattern: autoHighlightRegex,
						component: (props) => (
							<RendererDefinitionTextHighlighter
								{...props}
								definitionConfig={definitionConfig}
								keyPhrases={keyPhrases}
								sourceConfig={sourceConfig}
							/>
						),
					}
				: undefined;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[textHighlighterDependency],
	);

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE} attributes={fabricErrorBoundaryAttributes}>
			{contentStatusError && <ErrorDisplay error={contentStatusError} />}
			<LegacyWrmRenderer wrmType="css" />
			<Subscribe to={[BannerStateContainer]}>
				{(bannerState: BannerStateContainer) => (
					<div>
						<div id="selection-portal" />
						<PageSegmentLoadStart key={contentId} metric={CONTENT_RENDERER_METRIC} />
						<MediaAnalytics contentId={contentId}>
							<RendererTTIMarker
								revision={revision}
								renderWhenReady={memoRendererTTIMarkerRenderWhenReady}
							>
								{/**
								 * WARN: id and className on a wrapping div are required for some legacy macros to be properly
								 * attached to the elements in the page content
								 */}
								<StyledRenderer
									data-testid="pageContentRendererTestId"
									data-vc="pageContentRendererTestId"
									id="main-content"
									ref={setRootEl}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className="wiki-content"
									data-test-appearance={contentAppearance}
									isFabric
									bannerHeight={bannerState.getTotalHeight() || 0}
									markerRefs={markerRefs}
									intl={intl}
									isChildrenFGEnabled={fg('confluence_exclude_children_macro_from_blocklist')}
								>
									<ExtrensionIndexProvider document={adfDocument}>
										<AnalyticsListener onEvent={handleAnalyticsEvents(contentId)} channel="editor">
											<MultiMacroQueryContainer
												contentId={contentId}
												revision={revision}
												featureFlags={featureFlags}
												contentRendererMode="renderer"
												useServerRenderedData={renderServerPlaceholder}
												readFragmentInterop={readFragmentInterop}
											>
												{/**
												 * StyledRenderer above exists also to reconcile styles that come from AkRenderer / Atlaskit
												 * and CSS loaded via Superbatch. While superbatch CSS is loaded later, order of precedence
												 * for Atlaskit styles and styles from this module is non-deterministic, so StyledRenderer
												 * styles can be overridden by Ak styles.
												 * To avoid this the wrapping div below is used to make StyledRenderer styles more specific.
												 */}
												{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
												<div className="renderer-overrides">
													<ReadingAidsInvisibleLoadingPortal />
													<WithAnnotationsWrapper
														isArchived={isArchived}
														annotationProvider={annotationProvider}
														rendererRef={localRef}
														adfDocument={adfDocument}
														updateDocument={updateDocument}
														isNestedRender={false}
														lastModifiedDate={lastModifiedDate}
													>
														<ADFRenderer
															// WARN: AkRenderer relies on remount to correctly initialize media providers, which
															// must be re-initialized whenever we show new content. Also under certain circumstances
															// simple modification of the content can mangle the content displayed by AK renderer.
															// Revision is used as content can change upon reload initiated by Very Quick Reload™ feature.
															// See https://product-fabric.atlassian.net/browse/ED-10791 for details.
															key={revision}
															appearance={contentAppearance}
															contentMode={contentMode}
															contentId={contentId}
															document={adfDocument}
															eventHandlers={eventHandlers}
															extensionHandlers={extensionHandlers}
															portal={rootEl ?? undefined}
															renderWhenReady={memoAdfRendererRenderWhenReady}
															includeNodesCountInStats={false}
															onComplete={handleOnComplete}
															dataProviders={dataProviders}
															queryHash={queryHash}
															allowSelectAllTrap
															isContentHeaderFixedAtTop={isContentHeaderFixedAtTop}
															allowAnnotations={hasInlineComments}
															annotationProvider={annotationProvider}
															innerRef={localRef}
															textHighlighter={
																fg('kd_definitions_confluence_enable_platform')
																	? memoTextPlatformHighlighter
																	: memoTextHighlighter
															}
															disableHeadingIDs={renderServerPlaceholder || hideHeadingIds}
															extensionViewportSizes={extensionViewportSizes}
															getExtensionHeight={
																fg('confluence_connect_renderer_height_preset')
																	? getCalculatedHeightFromNode
																	: undefined
															}
															UNSTABLE_isPresentational={isPresentational}
															createSerializer={createSerializer}
															allowRendererContainerStyles={
																pageMode === PageMode.VIEW && fg('platform-ssr-table-resize')
															}
															{...rest}
														/>
													</WithAnnotationsWrapper>
												</div>
											</MultiMacroQueryContainer>
										</AnalyticsListener>
									</ExtrensionIndexProvider>
								</StyledRenderer>
								{renderComplete && (
									<>
										<MentionScrollComponent />
										<TaskScrollComponent />
									</>
								)}
								{displayInlineCommentNudger && state?.isContentReady && !isArchived && (
									<>
										<InlineCommentNudge
											key={revision}
											contentId={contentId}
											contentAppearance={contentAppearance}
										/>
										<InlineCommentLinkHandler />
									</>
								)}
								{isInReattachMode() && (
									<ReattachCommentPanelLoader
										containerId={contentId}
										updateDocument={updateDocument}
									/>
								)}
							</RendererTTIMarker>
						</MediaAnalytics>
					</div>
				)}
			</Subscribe>
			<LegacyWrmRenderer wrmType="js" renderComplete={renderComplete} />
		</ErrorBoundary>
	);
};

export const PageContentRendererFabric: typeof PageContentRendererFabricInternal = (props) => (
	<UFOSegment name="page-content-renderer-fabric">
		<PageContentRendererFabricInternal {...props} />
	</UFOSegment>
);
